import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Dropdown, Modal, Spinner } from 'react-bootstrap';
import { 
  FaSearch, 
  FaCalendarAlt, 
  FaFilter, 
  FaEye, 
  FaEdit, 
  FaTrash,
  FaMapMarkerAlt,
  FaUserAlt,
  FaCheckCircle,
  FaClock,
  FaBan,
  FaCalendarPlus,
  FaDownload,
  FaEllipsisV,
  FaFileExcel,
  FaFilePdf,
  FaPhone,
  FaDollarSign,
  FaTimes
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/admin/dashboard/AllEvents.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const AllEvents = () => {
  // State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(8);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [exportLoading, setExportLoading] = useState({ pdf: false, excel: false });

  // Form State for Create Event Modal
  const [formData, setFormData] = useState({
    eventName: '',
    eventType: '',
    eventDate: '',
    eventTime: '',
    customerName: '',
    customerContact: '',
    venue: '',
    guestCount: '',
    totalAmount: '',
    advancePaid: '',
    paymentStatus: 'Pending',
    notes: ''
  });

  // Sample Events Data
  const [events, setEvents] = useState([
    {
      id: 1,
      name: 'Sarah & John Wedding',
      type: 'Wedding Ceremony',
      date: '2026-02-15',
      time: '4:00 PM',
      venue: 'Grand Palace Hall, Lahore',
      customer: 'Sarah Ahmed',
      status: 'Confirmed',
      guests: 300,
      icon: '💒'
    },
    {
      id: 2,
      name: 'Ahmed & Fatima Engagement',
      type: 'Engagement Party',
      date: '2026-02-20',
      time: '6:00 PM',
      venue: 'Royal Garden Resort, Islamabad',
      customer: 'Ahmed Hassan',
      status: 'Pending',
      guests: 150,
      icon: '💍'
    },
    {
      id: 3,
      name: 'Ali & Zara Mehndi',
      type: 'Mehndi Ceremony',
      date: '2026-02-25',
      time: '7:00 PM',
      venue: 'Pearl Banquet, Karachi',
      customer: 'Ali Khan',
      status: 'Confirmed',
      guests: 200,
      icon: '🎨'
    },
    {
      id: 4,
      name: 'Usman & Ayesha Reception',
      type: 'Wedding Reception',
      date: '2026-03-01',
      time: '8:00 PM',
      venue: 'Crystal Palace, Faisalabad',
      customer: 'Usman Sheikh',
      status: 'Cancelled',
      guests: 500,
      icon: '🎊'
    },
    {
      id: 5,
      name: 'Hassan & Maryam Nikah',
      type: 'Nikah Ceremony',
      date: '2026-03-05',
      time: '11:00 AM',
      venue: 'Green Valley Lawn, Multan',
      customer: 'Hassan Raza',
      status: 'Confirmed',
      guests: 250,
      icon: '🕌'
    },
    {
      id: 6,
      name: 'Birthday Celebration',
      type: 'Birthday Party',
      date: '2026-03-10',
      time: '5:00 PM',
      venue: 'Fun World Arena, Gujranwala',
      customer: 'Zainab Hussain',
      status: 'Pending',
      guests: 80,
      icon: '🎂'
    },
    {
      id: 7,
      name: 'Corporate Dinner',
      type: 'Corporate Event',
      date: '2026-03-15',
      time: '7:30 PM',
      venue: 'PC Hotel, Lahore',
      customer: 'Tech Solutions Ltd',
      status: 'Confirmed',
      guests: 120,
      icon: '🏢'
    },
    {
      id: 8,
      name: 'Anniversary Party',
      type: 'Anniversary',
      date: '2026-03-20',
      time: '6:00 PM',
      venue: 'Sunset Beach Resort, Karachi',
      customer: 'David & Lisa',
      status: 'Completed',
      guests: 100,
      icon: '💑'
    }
  ]);

  // Stats Data
  const stats = [
    { label: 'Total Events', value: '156', color: 'purple', icon: '📅', trend: '+12%' },
    { label: 'Upcoming', value: '45', color: 'blue', icon: '⏰', trend: '+8%' },
    { label: 'Confirmed', value: '98', color: 'green', icon: '✅', trend: '+23%' },
    { label: 'Pending', value: '13', color: 'orange', icon: '⏳', trend: '-5%' }
  ];

  // Venue Options
  const venueOptions = [
    'Grand Palace Hall, Lahore',
    'Royal Garden Resort, Islamabad',
    'Pearl Banquet, Karachi',
    'Crystal Palace, Faisalabad',
    'Green Valley Lawn, Multan',
    'Fun World Arena, Gujranwala',
    'Sunset Beach Resort, Karachi',
    'PC Hotel, Lahore'
  ];

  // Event Type Options
  const eventTypes = [
    'Wedding Ceremony',
    'Mehndi Ceremony',
    'Nikah Ceremony',
    'Engagement Party',
    'Wedding Reception',
    'Birthday Party',
    'Anniversary',
    'Corporate Event'
  ];

  // Filter events
  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.venue.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = dateFilter === 'all' || 
                       (dateFilter === 'upcoming' && new Date(event.date) > new Date()) ||
                       (dateFilter === 'past' && new Date(event.date) < new Date()) ||
                       (dateFilter === 'today' && event.date === new Date().toISOString().split('T')[0]);
    
    const matchesStatus = statusFilter === 'all' || 
                         event.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesDate && matchesStatus;
  });

  // Pagination
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  // Handle Export PDF
  const handleExportPDF = async () => {
    setExportLoading({ ...exportLoading, pdf: true });
    
    // Simulate API call
    setTimeout(() => {
      setExportLoading({ ...exportLoading, pdf: false });
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Report exported successfully as PDF',
        confirmButtonText: 'OK',
        confirmButtonColor: '#7fd3c1',
        timer: 2000,
        timerProgressBar: true
      });
      
      // Here you would typically trigger actual PDF download
      console.log('Exporting PDF...');
    }, 1500);
  };

  // Handle Export Excel
  const handleExportExcel = async () => {
    setExportLoading({ ...exportLoading, excel: true });
    
    // Simulate API call
    setTimeout(() => {
      setExportLoading({ ...exportLoading, excel: false });
      
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Report exported successfully as Excel',
        confirmButtonText: 'OK',
        confirmButtonColor: '#7fd3c1',
        timer: 2000,
        timerProgressBar: true
      });
      
      // Here you would typically trigger actual Excel download
      console.log('Exporting Excel...');
    }, 1500);
  };

  // Handle Form Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle Create Event Submit
  const handleCreateEvent = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.eventName || !formData.eventType || !formData.eventDate || !formData.customerName) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields',
        text: 'Please fill all required fields!',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    // Create new event object
    const newEvent = {
      id: events.length + 1,
      name: formData.eventName,
      type: formData.eventType,
      date: formData.eventDate,
      time: formData.eventTime || '12:00 PM',
      venue: formData.venue,
      customer: formData.customerName,
      status: 'Pending',
      guests: parseInt(formData.guestCount) || 0,
      icon: getEventIcon(formData.eventType)
    };

    // Add to events array
    setEvents([newEvent, ...events]);

    // Show success message
    Swal.fire({
      icon: 'success',
      title: 'Event Created!',
      text: 'New event has been created successfully',
      confirmButtonText: 'OK',
      confirmButtonColor: '#7fd3c1',
      timer: 2000,
      timerProgressBar: true
    });

    // Reset form and close modal
    setFormData({
      eventName: '',
      eventType: '',
      eventDate: '',
      eventTime: '',
      customerName: '',
      customerContact: '',
      venue: '',
      guestCount: '',
      totalAmount: '',
      advancePaid: '',
      paymentStatus: 'Pending',
      notes: ''
    });
    setShowCreateModal(false);
  };

  // Get Event Icon based on type
  const getEventIcon = (type) => {
    const icons = {
      'Wedding Ceremony': '💒',
      'Mehndi Ceremony': '🎨',
      'Nikah Ceremony': '🕌',
      'Engagement Party': '💍',
      'Wedding Reception': '🎊',
      'Birthday Party': '🎂',
      'Anniversary': '💑',
      'Corporate Event': '🏢'
    };
    return icons[type] || '🎉';
  };

  // Handle Actions
  const handleView = (eventId) => {
    console.log('View event:', eventId);
  };

  const handleEdit = (eventId) => {
    console.log('Edit event:', eventId);
  };

  const handleDelete = (eventId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#7fd3c1',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setEvents(events.filter(event => event.id !== eventId));
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Event has been deleted.',
          confirmButtonColor: '#7fd3c1',
          timer: 2000
        });
      }
    });
  };

  const handleCancel = (eventId) => {
    console.log('Cancel event:', eventId);
  };

  // Get Status Badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Confirmed': { bg: 'success', icon: <FaCheckCircle /> },
      'Pending': { bg: 'warning', icon: <FaClock /> },
      'Cancelled': { bg: 'danger', icon: <FaBan /> },
      'Completed': { bg: 'info', icon: '✨' }
    };
    return statusConfig[status] || statusConfig['Pending'];
  };

  return (
    <AdminLayout>
    <div className="em-events-management">
      <Container fluid>
        {/* Page Header */}
        <div className="em-page-header">
          <div className="em-header-content">
            <h2 className="em-page-title">All Events</h2>
            <p className="em-page-subtitle">Manage and organize all wedding events</p>
          </div>
          <div className="em-header-actions">
            {/* Export PDF Button */}
            <Dropdown>
              <Dropdown.Toggle className="em-action-btn em-btn-secondary" disabled={exportLoading.pdf || exportLoading.excel}>
                {exportLoading.pdf || exportLoading.excel ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <FaDownload className="em-btn-icon" />
                )}
                Export Report
              </Dropdown.Toggle>

              <Dropdown.Menu className="em-export-dropdown">
                <Dropdown.Item onClick={handleExportPDF} disabled={exportLoading.pdf}>
                  {exportLoading.pdf ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FaFilePdf className="em-dropdown-icon em-pdf-icon" />
                      Export as PDF
                    </>
                  )}
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportExcel} disabled={exportLoading.excel}>
                  {exportLoading.excel ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="em-dropdown-icon em-excel-icon" />
                      Export as Excel
                    </>
                  )}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Create New Event Button */}
            <Button className="em-action-btn em-btn-primary" onClick={() => setShowCreateModal(true)}>
              <FaCalendarPlus className="em-btn-icon" />
              Create New Event
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="em-stats-row">
          {stats.map((stat, index) => (
            <Col key={index} xl={3} lg={6} md={6} sm={12} className="mb-4">
              <Card className={`em-stat-card em-stat-${stat.color}`}>
                <Card.Body className="em-stat-body">
                  <div className="em-stat-left">
                    <div className="em-stat-icon">{stat.icon}</div>
                    <div className="em-stat-info">
                      <div className="em-stat-value">{stat.value}</div>
                      <div className="em-stat-label">{stat.label}</div>
                    </div>
                  </div>
                  <div className="em-stat-trend">
                    <span className={`em-trend-badge ${stat.trend.includes('+') ? 'em-trend-up' : 'em-trend-down'}`}>
                      {stat.trend}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Main Content Card */}
        <Card className="em-content-card">
          <Card.Body className="em-card-body">
            {/* Filters Section */}
            <div className="em-filters-section">
              <Row>
                {/* Search Bar */}
                <Col lg={5} md={12} sm={12}>
                  <div className="em-filter-group">
                    <label className="em-filter-label">
                      <FaSearch className="em-label-icon" />
                      Search Events
                    </label>
                    <div className="em-search-wrapper">
                      <FaSearch className="em-input-icon" />
                      <Form.Control
                        type="text"
                        placeholder="Search by event, customer or venue..."
                        className="em-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </Col>

                {/* Date Filter */}
                <Col lg={3} md={6} sm={12}>
                  <div className="em-filter-group">
                    <label className="em-filter-label">
                      <FaCalendarAlt className="em-label-icon" />
                      Filter by Date
                    </label>
                    <div className="em-select-wrapper">
                      <FaCalendarAlt className="em-input-icon" />
                      <Form.Select
                        className="em-filter-select"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                      >
                        <option value="all">All Dates</option>
                        <option value="today">Today</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="past">Past Events</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>

                {/* Status Filter */}
                <Col lg={3} md={6} sm={12}>
                  <div className="em-filter-group">
                    <label className="em-filter-label">
                      <FaFilter className="em-label-icon" />
                      Filter by Status
                    </label>
                    <div className="em-select-wrapper">
                      <FaFilter className="em-input-icon" />
                      <Form.Select
                        className="em-filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="pending">Pending</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>

                {/* Results Count */}
                <Col lg={1} md={6} sm={12}>
                  <div className="em-filter-group">
                    <label className="em-filter-label em-label-invisible">Results</label>
                    <div className="em-results-badge">
                      <span className="em-result-count">{currentEvents.length}</span>
                      <span className="em-result-text">of {filteredEvents.length}</span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Events Table */}
            <div className="em-table-wrapper">
              <Table responsive className="em-table">
                <thead className="em-table-head">
                  <tr>
                    <th className="em-th">ID</th>
                    <th className="em-th">Event Name</th>
                    <th className="em-th">Event Type</th>
                    <th className="em-th">Event Date</th>
                    <th className="em-th">Venue</th>
                    <th className="em-th">Customer</th>
                    <th className="em-th">Status</th>
                    <th className="em-th em-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody className="em-table-body">
                  {currentEvents.length > 0 ? (
                    currentEvents.map((event) => (
                      <tr key={event.id} className="em-table-row">
                        <td className="em-td em-td-id">
                          <span className="em-id-badge">#{event.id.toString().padStart(3, '0')}</span>
                        </td>
                        <td className="em-td">
                          <div className="em-event-info">
                            <div className="em-event-icon">{event.icon}</div>
                            <div className="em-event-details">
                              <span className="em-event-name">{event.name}</span>
                              <span className="em-event-guests">
                                <FaUserAlt className="em-guests-icon" />
                                {event.guests} Guests
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="em-td">
                          <span className="em-event-type">{event.type}</span>
                        </td>
                        <td className="em-td">
                          <div className="em-date-info">
                            <span className="em-date">{new Date(event.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                            <span className="em-time">
                              <FaClock className="em-clock-icon" />
                              {event.time}
                            </span>
                          </div>
                        </td>
                        <td className="em-td em-td-venue">
                          <div className="em-venue-info">
                            <FaMapMarkerAlt className="em-venue-icon" />
                            <span className="em-venue-text">{event.venue}</span>
                          </div>
                        </td>
                        <td className="em-td em-td-customer">
                          <div className="em-customer-info">
                            <div className="em-customer-avatar">
                              {event.customer.charAt(0)}
                            </div>
                            <span className="em-customer-name">{event.customer}</span>
                          </div>
                        </td>
                        <td className="em-td">
                          <Badge 
                            bg={getStatusBadge(event.status).bg} 
                            className="em-status-badge"
                          >
                            {getStatusBadge(event.status).icon} {event.status}
                          </Badge>
                        </td>
                        <td className="em-td em-td-actions">
                          <div className="em-action-buttons">
                            <button 
                              className="em-icon-btn em-btn-view"
                              onClick={() => handleView(event.id)}
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            <button 
                              className="em-icon-btn em-btn-edit"
                              onClick={() => handleEdit(event.id)}
                              title="Edit Event"
                            >
                              <FaEdit />
                            </button>
                            <Dropdown className="em-more-dropdown" align="end">
                              <Dropdown.Toggle className="em-icon-btn em-btn-more" variant="link">
                                <FaEllipsisV />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="em-dropdown-menu">
                                <Dropdown.Item onClick={() => handleView(event.id)}>
                                  <FaEye className="em-dropdown-icon" /> View Full Details
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleCancel(event.id)}>
                                  <FaBan className="em-dropdown-icon" /> Cancel Event
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => handleDelete(event.id)} className="em-dropdown-danger">
                                  <FaTrash className="em-dropdown-icon" /> Delete Event
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="em-no-data">
                        <div className="em-no-data-content">
                          <span className="em-no-data-icon">📅</span>
                          <p className="em-no-data-text">No events found</p>
                          <p className="em-no-data-subtext">Try adjusting your filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredEvents.length > eventsPerPage && (
              <div className="em-pagination">
                <Button 
                  className="em-page-btn em-prev-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                
                <div className="em-page-numbers">
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      className={`em-page-number ${currentPage === index + 1 ? 'em-active' : ''}`}
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                <Button 
                  className="em-page-btn em-next-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Create Event Modal */}
      <Modal 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)}
        size="lg"
        centered
        className="em-create-modal"
      >
        <Modal.Header className="em-modal-header">
          <Modal.Title className="em-modal-title">
            <FaCalendarPlus className="em-modal-icon" />
            Create New Event
          </Modal.Title>
          <button className="em-modal-close" onClick={() => setShowCreateModal(false)}>
            <FaTimes />
          </button>
        </Modal.Header>
        <Modal.Body className="em-modal-body">
          <Form onSubmit={handleCreateEvent}>
            {/* Event Details Section */}
            <div className="em-form-section">
              <h5 className="em-section-title">Event Details</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">
                      Event Name <span className="em-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="eventName"
                      value={formData.eventName}
                      onChange={handleInputChange}
                      placeholder="Enter event name"
                      className="em-form-input"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">
                      Event Type <span className="em-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleInputChange}
                      className="em-form-input"
                      required
                    >
                      <option value="">Select Event Type</option>
                      {eventTypes.map((type, idx) => (
                        <option key={idx} value={type}>{type}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">
                      Event Date <span className="em-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleInputChange}
                      className="em-form-input"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">Event Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="eventTime"
                      value={formData.eventTime}
                      onChange={handleInputChange}
                      className="em-form-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Customer Information Section */}
            <div className="em-form-section">
              <h5 className="em-section-title">Customer Information</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">
                      Customer Name <span className="em-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      placeholder="Enter customer name"
                      className="em-form-input"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">Customer Contact</Form.Label>
                    <Form.Control
                      type="tel"
                      name="customerContact"
                      value={formData.customerContact}
                      onChange={handleInputChange}
                      placeholder="Enter contact number"
                      className="em-form-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Venue & Capacity Section */}
            <div className="em-form-section">
              <h5 className="em-section-title">Venue & Capacity</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">
                      Select Venue <span className="em-required">*</span>
                    </Form.Label>
                    <Form.Select
                      name="venue"
                      value={formData.venue}
                      onChange={handleInputChange}
                      className="em-form-input"
                      required
                    >
                      <option value="">Select Venue</option>
                      {venueOptions.map((venue, idx) => (
                        <option key={idx} value={venue}>{venue}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">Guest Count</Form.Label>
                    <Form.Control
                      type="number"
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleInputChange}
                      placeholder="Enter number of guests"
                      className="em-form-input"
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Payment Info Section */}
            <div className="em-form-section">
              <h5 className="em-section-title">Payment Info</h5>
              <Row>
                <Col md={4}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">Total Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="totalAmount"
                      value={formData.totalAmount}
                      onChange={handleInputChange}
                      placeholder="Enter total amount"
                      className="em-form-input"
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">Advance Paid</Form.Label>
                    <Form.Control
                      type="number"
                      name="advancePaid"
                      value={formData.advancePaid}
                      onChange={handleInputChange}
                      placeholder="Enter advance amount"
                      className="em-form-input"
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="em-form-group">
                    <Form.Label className="em-form-label">Payment Status</Form.Label>
                    <Form.Select
                      name="paymentStatus"
                      value={formData.paymentStatus}
                      onChange={handleInputChange}
                      className="em-form-input"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                      <option value="Partial">Partial</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Additional Notes Section */}
            <div className="em-form-section">
              <h5 className="em-section-title">Additional Notes</h5>
              <Form.Group className="em-form-group">
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Enter any additional notes or special requirements..."
                  className="em-form-textarea"
                />
              </Form.Group>
            </div>

            {/* Modal Footer Buttons */}
            <div className="em-modal-footer-buttons">
              <Button 
                type="button" 
                className="em-modal-btn em-btn-cancel"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="em-modal-btn em-btn-submit"
              >
                <FaCalendarPlus className="me-2" />
                Create Event
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
    </AdminLayout>
  );
};

export default AllEvents;